import { debounce } from "lodash"
import * as React from "react"
import { Control, Emitter } from "rete"
import { EventsTypes } from "rete/types/events"

type NumericConstantRowProps = {
  getValue: () => string
  setValue: (value: string) => void
  getExpose: () => boolean
  setExpose: (value: boolean) => void
}

const EXPOSE_ON_COLOR = "#6ecf8a"
const EXPOSE_OFF_COLOR = "#5a7a62"

const NumericConstantRowField = ({
  getValue,
  setValue,
  getExpose,
  setExpose,
}: NumericConstantRowProps) => {
  const [, forceUpdate] = React.useReducer(x => x + 1, 0)
  const exposed = getExpose()

  return (
    <div
      className="numeric-constant-row d-flex align-items-center"
      style={{ gap: "0.5rem", padding: "0 0.5rem 0.5rem", width: "100%" }}
    >
      <input
        type="text"
        className="form-control"
        style={{ flex: 1, minWidth: 0, width: "100%" }}
        value={getValue()}
        onChange={e => {
          setValue(e.target.value)
          forceUpdate()
        }}
        onPointerDown={e => e.stopPropagation()}
        onDoubleClick={e => e.stopPropagation()}
        aria-label="Numeric constant value"
      />
      <button
        type="button"
        className="numeric-constant-row__expose btn btn-link p-0 border-0 d-flex align-items-center justify-content-center"
        style={{
          color: exposed ? EXPOSE_ON_COLOR : EXPOSE_OFF_COLOR,
          textDecoration: "none",
          flexShrink: 0,
          width: "1.5rem",
          opacity: exposed ? 1 : 0.75,
        }}
        aria-pressed={exposed}
        aria-label={exposed ? "Shown in detail panel" : "Show in detail panel"}
        title={exposed ? "Shown in detail panel" : "Show in detail panel"}
        onClick={e => {
          e.stopPropagation()
          setExpose(!exposed)
          forceUpdate()
        }}
        onPointerDown={e => e.stopPropagation()}
        onDoubleClick={e => e.stopPropagation()}
      >
        <i className={`fas fa-eye${exposed ? "" : "-slash"}`} style={{ fontSize: "1rem" }} />
      </button>
    </div>
  )
}

export class NumericConstantRowControl extends Control {
  props: NumericConstantRowProps
  component: (props: NumericConstantRowProps) => JSX.Element
  type: string

  constructor(emitter: Emitter<EventsTypes> | null, valueKey: string, exposeKey: string) {
    super(`${valueKey}:${exposeKey}`)
    this.type = "NumericConstantRowControl"

    const valueProcess = debounce(() => emitter?.trigger("process"), 1000)
    const exposeNotify = debounce(() => emitter?.trigger("process"), 300)

    const readValue = (): string => {
      const raw = this.getData(valueKey)
      if (typeof raw === "number" && !Number.isNaN(raw)) {
        return String(raw)
      }
      return typeof raw === "string" ? raw : ""
    }

    this.props = {
      getValue: readValue,
      setValue: value => {
        this.putData(valueKey, value)
        valueProcess()
      },
      getExpose: () => Boolean(this.getData(exposeKey)),
      setExpose: value => {
        this.putData(exposeKey, value)
        exposeNotify()
      },
    }
    this.component = NumericConstantRowField
  }
}
